import { useEffect, useMemo } from "react";
import { Clients, useQuery } from "../apollo";
import { AWSRegions, Consent } from "../generated/ups/graphql";
import { GetUserConsentsQuery } from "../graphql/queries/getUserConsents";
import { GetUserRequestType, UIUserStatus } from "../types/user";

type GetUserConsentsResponseType = {
  getUser: { consents: Consent[]; status: UIUserStatus };
};

type ConsentsResponse = {
  isLoading: boolean;
  error: Error | null;
  consents: Consent[] | null;
  user: {
    status: UIUserStatus;
    username?: string;
    region?: AWSRegions;
  };
};

export type UseGetUserConsents<
  T extends GetUserRequestType | null = GetUserRequestType | null,
> = (user: T) => ConsentsResponse;

export const useGetUserConsents: UseGetUserConsents = (user) => {
  const variables = useMemo(() => {
    if (user?.region && user?.username) {
      return { username: user.username, region: user.region };
    }
    return undefined;
  }, [user?.username, user?.region]);
  const { data, loading, refetch, error } = useQuery<
    GetUserConsentsResponseType,
    GetUserRequestType
  >(Clients.UPS, GetUserConsentsQuery, {
    skip: !variables,
    variables,
  });

  useEffect(() => {
    if (variables) {
      refetch(variables);
    }
  }, [variables, refetch]);

  return {
    isLoading: loading,
    consents: data?.getUser.consents || null,
    user: {
      username: variables?.username,
      region: variables?.region,
      status: data?.getUser.status ?? UIUserStatus.INACTIVE,
    },
    error: error as Error,
  };
};
